import {
  CellClickedEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  IRowDragItem,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ISetFilter,
  ITextFilterParams,
  ModuleRegistry,
  PaginationChangedEvent,
  RowDragEndEvent,
  RowDragEnterEvent,
  RowDropZoneParams,
  RowSelectedEvent,
  RowStyle,
  SetFilterValuesFuncParams,
  SideBarDef,
  SortChangedEvent
} from "@ag-grid-community/core";
import {AgGridReact} from "@ag-grid-community/react";
import {ColumnsToolPanelModule} from "@ag-grid-enterprise/column-tool-panel";
import {ServerSideRowModelModule} from "@ag-grid-enterprise/server-side-row-model";
import {SetFilterModule} from "@ag-grid-enterprise/set-filter";
import {ApolloError, ApolloQueryResult, useApolloClient} from "@apollo/client";
import {Button, ControlGroup, Icon, Intent, Text} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {subDays} from "date-fns";
import {RuleProperties} from "json-rules-engine";
import {debounce, isEqual, isNumber} from "lodash";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Fill, Top} from "react-spaces";
import log from "loglevel";
import {useAppContext} from "../../ApplicationContext";
import {
  Job,
  JobAttributeFilter,
  Manifest,
  OrderRequirement,
  SkillOverrideNoteInput,
  useAssignJobsToManifestMutation,
  useGetDistinctValuesLazyQuery,
  useSetUserPrefMutation
} from "../../generated/graphql";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  PreferenceContext,
  PrefValue
} from "../../providers/PreferenceProvider";
import {navigateToCmsOrder} from "../../services/CmsService";
import {
  formatDate,
  getDateTimeAtSpecificTimeZone,
  getLocalDate,
  getLocalDateTime,
  getLocalTime,
  getTimeAtSpecificTimeZone
} from "../../utils/DateTimeUtils";
import {copyToClipboard, getArrayFromField} from "../../utils/General";
import {buildDispatchGroupFilter} from "../../utils/FilterUtils";
import {AssignmentViewActionTypes, DragAndDropTargetPayload} from "../../providers/AssignmentViewReducer";
import {AuthState, useAuthState} from "../../providers/AuthProvider";
import ActiveFilterDialog, {IActiveFilterDialogProps, IActiveFilterDialogRef} from "../common/ActiveFilterDialog";
import JobNumberCellRenderer from "../common/cell-renderers/JobNumberCellRenderer";
import ColorCellRenderer from "../common/cell-renderers/ColorCellRenderer";
import doubleFieldCellRenderer, {DoubleFieldCellValue} from "../common/cell-renderers/DoubleFieldCellRenderer";
import JobStatusCellRenderer from "../common/cell-renderers/JobStatusCellRenderer";
import MapIconCellRenderer from "../common/cell-renderers/MapIconCellRenderer";
import serviceTypeCellRenderer from "../common/cell-renderers/ServiceTypeCellRenderer";
import {ALLOWED_SAVED_PREFERENCE_SOURCES, TenantPreferences, UserPreferences} from "../../constants/constants";
import CustomerFilter from "../common/CustomerFilter";
import DateFilter from "../common/DateFilter";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import {DispatchRulesDataContext, DispatchRulesDataState} from "../../providers/DispatchRulesDataProvider";

import GridSettingsToolPanel from "../common/GridSettingsToolPanel";
import {JobRequirement, PendingAssignment} from "../../providers/AssignmentReducer";
import {JobAssignmentSubmissionResult} from "../../utils/assignment/JobAssignmentEventHandler";
import {AppToaster, showAssignmentToast} from "../../utils/toasters/toaster";
import MultiSelectDropDown, {MultiSelectDropDownItem} from "../common/MultiSelectDropDown";
import Search from "../common/Search";
import {useGridRefreshOnInterval} from "../../hooks/useGridRefreshOnInterval";
import {useColorColumnSortControl} from "../../hooks/useColorColumnSortControl";
import {AssignJobsConfirmation} from "../manifest/details/components/AssignJobsConfirmation";
import {EverCompletedCheckDialog} from "../common/EverCompletedCheckDialog";
import {TManifestEnhanced} from "../common/types";

import {useJobsEverCompleted} from "../../hooks/useJobsEverCompleted";
import {getHighestRankedColor, NO_COLOR_FILTER_VALUE} from "../../utils/color-indicator/color-indicator.utils";
import {
  EntityVisibilityLevel,
  MapVisibilityAttributes,
  UnassignedJobsVisibilityTypes,
  useMapVisibilityDispatch,
  useMapVisibilityState
} from "../map/MapVisibilityContext";
import {DriverRequirementEnforcementLevel} from "../settings/enums/DriverRequirementEnforcementLevel";
import AssignmentBarV2 from "./components/AssignmentBarV2";
import SuggestErrorDialog from "./components/SuggestErrorDialog";
import {useSuggestedAssignmentContext} from "../../providers/SuggestedAssignmentProvider";
import "./JobPanel.css";
import {createJobsServerSideDatasource} from "../../services/datasources/JobServerSideDataSource.service";
import {
  DefaultTimeFilterCustomValue,
  getTimeOptionFilterModel,
  isCustomOption,
  PlacedInLastFilterOptions,
  PlacedInLastFilterSelect,
  ReadyDueFilterOptions,
  ReadyDueTimeFilterSelect,
  TimeOption
} from "../common/time-filter-select/TimeQuickFilterSelect";
import {deliveryStop, isCompleted, pickupStop} from "../../utils/stops/StopUtils";
import {useOktaAuth} from "@okta/okta-react";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../providers/DispatchStationProvider";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import {CellRendererWithToolTip} from "../common/cell-renderers/CellRendererWithToolTip";
import {useAssignmentDispatch, useAssignmentState} from "../../providers/AssignmentProdiver";
import {useAssignmentViewDispatch, useAssignmentViewState} from "../../providers/AssignmentViewProvider";
import BadDataOverlay from "../common/BadDataOverlay";
import {WarningIcon} from "../common/icons";
import {StyledButton} from "../common/StyledButtons";
import {
  AG_GRID_NOT_EXIST_FILTER,
  getFilterValues,
  getJobRowClass,
  getOrderSkillFilterParams,
  JobGridOptions,
  orderSkillsValueGetter
} from "../../utils/ag-grid/AGGridUtils";
import {useRedrawPendingRows} from "../../hooks/useRedrawPendingRows";
import {ColorIndicatorCellRenderer} from "../common/cell-renderers/ColorIndicatorCellRenderer";
import {buildSkillOverrideNotes, getMatchRequirementManifests} from "../../utils/assignment/Assignment.utils";
import {DispatchNotesProvider} from "../../providers/DispatchNotesProvider";
import {useManifestDetailsState} from "../../providers/ManifestDetailsProvider";

ModuleRegistry.registerModules([ServerSideRowModelModule, SetFilterModule, ColumnsToolPanelModule]);

const PanelContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: #ededed;

  .ag-pinned-left-header {
    border-right: none;
  }

  .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell) {
    border-right: none;
  }
  .drag-column .ag-drag-handle {
    margin-left: 4px;
    margin-right: 8px;
  }
  .job-number-cell .ag-drag-handle {
    display: none;
  }
`;

const ButtonBar = styled(Top)<{hasWarningMessage: boolean}>`
  padding: ${(props) => (props.hasWarningMessage ? "16px 0 8px 0" : "")};
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.hasWarningMessage ? "space-between" : "center")};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LeftControls = styled.div`
  display: flex;
  margin-right: auto;
  margin-left: 25px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const RightControls = styled.div`
  display: flex;
  margin-right: 18px;
  align-items: center;
  justify-content: flex-start;
`;

export const WarningText = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-family: "Roboto", sans-serif;
  color: #161616;
  display: grid;
  grid-template-columns: 18px auto;
  align-items: center;
  gap: 5px;
`;

const AssignmentBarV2Pinned = styled(AssignmentBarV2)`
  position: absolute;
  bottom: 95px;
  left: 0;
  right: 0;
  margin: auto;
`;

type JobPanelProps = {
  dragAndDropTargets: DragAndDropTargetPayload[];
  currentDragAndDropTargets: RowDropZoneParams[];
  getGridRef?: (gridRef: AgGridReact<AgJob>) => void;
};

export type GridContext = {
  globalQuery?: string;
  globalFilter?: JobAttributeFilter;
};

export type ExtendedAgJob = AgJob & {hide: boolean};

export interface AgJob extends Job {
  map?: MapVisibilityAttributes;
  indicatorColor?: string | null;
  hide?: boolean;
}

export const getColorPriority = (colorRules: RuleProperties[] | undefined, color: string | null | undefined) => {
  return colorRules?.find((rule) => rule.event.params?.color === color)?.priority ?? 0;
};

export enum WARNING_MESSAGES {
  FILTER = "Color filtering is limited to the current page",
  SORT = "Color sorting is limited to the current page",
  FILTER_AND_SORT = "Color filtering/sorting is limited to the current page"
}

export const getWarningMessage = (isSorting: boolean, isFiltering: boolean) => {
  if (isSorting && isFiltering) {
    return WARNING_MESSAGES.FILTER_AND_SORT;
  }
  if (isSorting) {
    return WARNING_MESSAGES.SORT;
  }
  if (isFiltering) {
    return WARNING_MESSAGES.FILTER;
  }
};

// With computedColumns on, these two colIds are filter carriers for the Ready/Due quick filter
// and must never be visible. They are kept in the grid solely so getFilterInstance() resolves.
export const LEGACY_READY_DUE_COL_IDS = ["stop.pickup.scheduledDateTime", "stop.delivery.scheduledDateTime"] as const;

/**
 * DP-3156: `applyColumnState({applyOrder: true})` only positions colIds that appear in the
 * saved state, so a column added after a user saved their layout lands detached from the
 * neighbour it is meant to sit beside. Splice it in when it is missing. Mirrors
 * `addETAColumnsState` in the assigned-stops grid.
 *
 * Carries nothing but the colId on purpose: `applyColumnState` only applies the attributes
 * present in an entry, so visibility keeps falling out of the column definition instead of
 * being pinned here and drifting from it.
 */
export const addNewColumnStateAfter = (columnState: ColumnState[], colId: string, afterColId: string) => {
  if (columnState.some((col) => col.colId === colId)) return columnState;
  const anchorIndex = columnState.findIndex((col) => col.colId === afterColId);
  if (anchorIndex === -1) return columnState;
  const clonedColumnState = columnState.map((col) => ({...col}));
  clonedColumnState.splice(anchorIndex + 1, 0, {colId});
  return clonedColumnState;
};

export const mapReadyDueColumnState = (columnState: ColumnState[]) => {
  const clonedColumnState = columnState.map((col) => ({...col}));

  const newReadyAtColumnState = clonedColumnState.find((col) => col.colId === "readyAt");
  const pickupColumnIndex = clonedColumnState.findIndex((column) => column.colId === "stop.pickup.scheduledDateTime");
  const pickupColumnState = clonedColumnState[pickupColumnIndex];
  if (!newReadyAtColumnState && pickupColumnState) {
    clonedColumnState.splice(pickupColumnIndex + 1, 0, {
      ...pickupColumnState,
      colId: "readyAt"
    });
  }
  if (pickupColumnState) {
    pickupColumnState.hide = true;
  }

  const newDueByColumnState = clonedColumnState.find((col) => col.colId === "dueBy");
  const deliveryColumnIndex = clonedColumnState.findIndex(
    (column) => column.colId === "stop.delivery.scheduledDateTime"
  );
  const deliveryColumnState = clonedColumnState[deliveryColumnIndex];
  if (!newDueByColumnState && deliveryColumnState) {
    clonedColumnState.splice(deliveryColumnIndex + 1, 0, {
      ...deliveryColumnState,
      colId: "dueBy"
    });
  }
  if (deliveryColumnState) {
    deliveryColumnState.hide = true;
  }

  return clonedColumnState;
};

export const ColumnTypes: {[key: string]: ColDef<Job>} = {
  filterColumn: {
    suppressMenu: false,
    menuTabs: ["filterMenuTab"],
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true
    }
  },
  textFilter: {
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains", "notContains", "equals", "notEqual", "blank", "notBlank"]
    } as ITextFilterParams
  },
  numberFilter: {
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: [
        "equals",
        "notEqual",
        "lessThan",
        "     lessThanOrEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "inRange"
      ]
    }
  }
};

export const CustomRowStyle: RowStyle = {
  //Prevent select text when Shift
  userSelect: "none",
  fontSize: "12px"
};

export const DefaultColumnDef = {
  resizable: true,
  sortable: false,
  minWidth: 40,
  initialWidth: 75,
  suppressMenu: true
} as ColDef;

export const SortableColumnFields: string[] = [
  "indicatorColor",
  "jobNumber",
  "readyAt",
  "dueBy",
  "stops.scheduledDateTime,stops.timeZone,stops.stopType",
  "order.customer.name",
  "stops.dispatchZone,stops.stopType",
  "service",
  "vehicleType.description",
  "stops.address,stops.stopType",
  "pieces",
  "weight",
  "dispatchGroup.name",
  "jobStatus",
  "order.caller",
  "order.callerPhone",
  "order.customer.alias",
  "order.customer.customerCode",
  "driver.driverCode",
  "site.code",
  "order.alias",
  "jobAlias",
  "order.auth",
  "order.orderId",
  "order.codAmount",
  "order.description",
  "order.priority.description",
  "routeNumber",
  "order.totalMiles",
  "order.origin",
  "order.packageType.description",
  "order.qualityFlag",
  "stops.city,stops.stopType",
  "stops.zip,stops.stopType",
  "stopCount",
  "order.createdDateTime",
  "order.declaredValue",
  "order.user.userName"
];

const JobPanel = ({dragAndDropTargets, currentDragAndDropTargets, getGridRef}: JobPanelProps) => {
  const {
    driverRequirements: driverRequirementsEnabled,
    colorSort: filterAndSortByColorsEnabled,
    computedColumns: computedColumnsEnabled,
    skills: skillsEnabled,
    multipleSort: multipleSortEnabled,
    dispatchNote: dispatchNotesEnabled,
    backendColorRules: backendColorRulesEnabled,
    suggestedAssignment: suggestedAssignmentEnabled
  } = useFeatureFlagContext();

  const [assignJobsToManifest] = useAssignJobsToManifestMutation();
  const [getDistinctValues] = useGetDistinctValuesLazyQuery();

  const assignmentState = useAssignmentState();
  const assignmentDispatch = useAssignmentDispatch();
  const assignmentViewState = useAssignmentViewState();
  const assignmentViewDispatch = useAssignmentViewDispatch();

  // Suggested Assignment
  const suggestedAssignment = useSuggestedAssignmentContext();

  const [showSuggestError, setShowSuggestError] = useState(false);

  useEffect(() => {
    if (suggestedAssignment.state === "capReached") {
      AppToaster.show({
        intent: Intent.WARNING,
        icon: "warning-sign",
        message: "You've reached the daily limit for suggested assignments. It will reset at 12:01 AM Eastern."
      });
    }
    if (suggestedAssignment.state === "failed" || suggestedAssignment.state === "validationError") {
      setShowSuggestError(true);
    }
  }, [suggestedAssignment.state]);

  const apolloClient = useApolloClient();
  const {appState} = useAppContext();
  const authState = useAuthState();
  const {oktaAuth} = useOktaAuth();
  const mapVisibilityState = useMapVisibilityState();
  const mapVisibilityDispatch = useMapVisibilityDispatch();
  const {userPreferences, tenantPreferences, userPrefsQueryRefetch, isUserPreferencesLoaded} =
    useContext(PreferenceContext);
  const {jobGroups, isDispatchGroupLoaded} = useDispatchGroupContext();
  const [setUserPref] = useSetUserPrefMutation();
  const [isInitialized, setInitialized] = useState<boolean>(false);
  const [isGridGroupInitialized, setGridGroupInitialized] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<AgJob>>(null);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const jobDialogRefs = useRef<IActiveFilterDialogRef>(null);
  const [selectedReadyDueFilterOption, setSelectedReadyDueFilterOption] = useState<TimeOption>();
  const [selectedPlacedInLastFilterOption, setSelectedPlacedInLastFilterOption] = useState<TimeOption>();
  const [isEnableQualified, setIsEnableQualified] = useState(false);
  const [datasourceFailed, setDatasourceFailed] = useState<ApolloError | undefined>();
  const ruleContextState = useContext<DispatchRulesDataState>(DispatchRulesDataContext);
  const {skills, isDispatchDataLoaded, services, vehicleTypes, sites, packageTypes, users, orderRequirements} =
    useDispatchDataContext();
  const [isOpeningCMSOrder, setIsOpeningCMSOrder] = useState<boolean>(false);

  const [gridContext, setGridContext] = useState<GridContext>({
    globalQuery: "",
    globalFilter: undefined
  });
  const [gridHasActiveFilters, setGridHasActiveFilters] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<WARNING_MESSAGES | undefined>();

  const [currentJobPanePage, setCurrentJobPanePage] = useState<number>();
  const [datasource, setDatasource] = useState<IServerSideDatasource>();
  const [selectedJobGroups, setSelectedJobGroups] = useState<string[] | null>(null);
  const dispatchStationState = useDispatchStationContext();
  const [showAssignJobsConfirm, setShowAssignJobsConfirm] = useState(false);
  const [showEverCompletedDialog, setShowEverCompletedDialog] = useState(false);
  const [everCompletedJobs, setEverCompletedJobs] = useState<
    Array<{jobId: number; jobNumber: string; orderId?: number}>
  >([]);
  const pendingConfirmActionRef = useRef<(() => void) | null>(null);
  const skillOverrideNotesRef = useRef<SkillOverrideNoteInput[]>([]);

  const {manifestDriverId} = useManifestDetailsState();
  useGridRefreshOnInterval(
    "Unassigned Jobs",
    gridRef,
    gridReady,
    datasourceFailed,
    undefined,
    manifestDriverId !== undefined
  );
  useRedrawPendingRows(gridRef, assignmentState.pendingActions, gridReady && isInitialized);

  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;
  const isSkillsBasedRequirements = extractSimplePref(
    tenantPreferences,
    TenantPreferences.skillsBasedRequirements,
    false
  ).value as boolean;

  const datasourceFailCallback = useCallback((error: ApolloError | undefined) => {
    setDatasourceFailed(error);
  }, []);

  const sortingColumns =
    gridRef.current?.columnApi?.getAllGridColumns()?.filter((col) => {
      return col.getSort();
    }) ?? [];

  useColorColumnSortControl({
    gridRef,
    gridReady,
    isInitialized,
    multipleSortEnabled,
    sortingColumns,
    sortableColumnFields: SortableColumnFields
  });

  const driverRequiredSetting = useMemo(() => {
    const prefVal: PrefValue<DriverRequirementEnforcementLevel> = extractSimplePref(
      tenantPreferences,
      TenantPreferences.driverRequirementEnforcement,
      DriverRequirementEnforcementLevel.Allow
    );
    return prefVal.value as DriverRequirementEnforcementLevel;
  }, [tenantPreferences]);

  const isSkillsBased = useMemo(() => {
    return isSkillsBasedRequirements && skillsEnabled;
  }, [isSkillsBasedRequirements, skillsEnabled]);

  const {getEverCompletedJobs} = useJobsEverCompleted();

  const jobGroupOptions = useMemo(() => {
    const map: Map<number, MultiSelectDropDownItem> = new Map();
    if (jobGroups.length > 0) {
      const groups = [...jobGroups];
      groups.sort((a, b) => a.dispatchGroup!.localeCompare(b.dispatchGroup!));
      groups.forEach((group) => {
        map.set(group.dispatchGroupId!, {
          name: group.dispatchGroup,
          selected: selectedJobGroups?.includes(group.dispatchGroup!),
          id: group.dispatchGroupId
        } as MultiSelectDropDownItem);
      });
    }
    return map;
  }, [jobGroups, selectedJobGroups]);

  const updateUserPrefsTimeFilter = useCallback(
    async (key: string, value: any) => {
      try {
        await setUserPref({
          variables: {
            name: key,
            input: createJsonPref(value, true)
          }
        }).then(() => {
          userPrefsQueryRefetch?.();
        });
      } catch (error) {
        log.error(error);
      }
    },
    [setUserPref, userPrefsQueryRefetch]
  );

  const resetViewGestured = useCallback(() => {
    if (mapVisibilityState.unassignedJobVisibilityType !== UnassignedJobsVisibilityTypes.NONE) {
      mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
    }
  }, [mapVisibilityDispatch, mapVisibilityState.unassignedJobVisibilityType]);

  const onConfirmSelectedTimeFilter = useCallback(
    (option: TimeOption) => {
      setSelectedReadyDueFilterOption(option);
      if (option.type === "ready") {
        gridRef.current!.api.getFilterInstance("stop.delivery.scheduledDateTime", (filter) => {
          filter?.setModel(null);
          gridRef.current!.api.onFilterChanged();
        });
        gridRef.current!.api.getFilterInstance("stop.pickup.scheduledDateTime", (filter) => {
          filter?.setModel(getTimeOptionFilterModel(option));
          gridRef.current!.api.onFilterChanged();
        });
      } else {
        gridRef.current!.api.getFilterInstance("stop.pickup.scheduledDateTime", (filter) => {
          filter?.setModel(null);
          gridRef.current!.api.onFilterChanged();
        });
        gridRef.current!.api.getFilterInstance("stop.delivery.scheduledDateTime", (filter) => {
          filter?.setModel(getTimeOptionFilterModel(option));
          gridRef.current!.api.onFilterChanged();
        });
      }
      resetViewGestured();
      updateUserPrefsTimeFilter(UserPreferences.jobGridFilterReadyDue, option);
      if (isCustomOption(option)) {
        const currentState = extractJsonPref(
          userPreferences,
          UserPreferences.jobGridFilterReadyDueCustomValue,
          DefaultTimeFilterCustomValue
        );

        updateUserPrefsTimeFilter(UserPreferences.jobGridFilterReadyDueCustomValue, {
          ...DefaultTimeFilterCustomValue,
          ...currentState.value,
          [option.type as string]: option.customValue
        });
      }
    },
    [resetViewGestured, updateUserPrefsTimeFilter, userPreferences]
  );

  const handleSelectedPlacedInLastTime = useCallback(
    (option: TimeOption) => {
      gridRef.current!.api.getFilterInstance("order.createdDateTime", (filter) => {
        setSelectedPlacedInLastFilterOption(option);
        filter?.setModel(getTimeOptionFilterModel(option));
        gridRef.current!.api.onFilterChanged();
      });
      updateUserPrefsTimeFilter(UserPreferences.jobGridFilterPlaced, option.title);
      resetViewGestured();
    },
    [updateUserPrefsTimeFilter, resetViewGestured]
  );

  const updateColumn = useCallback(
    (e: ColumnMovedEvent<Job> | SortChangedEvent<Job> | ColumnVisibleEvent<Job>) => {
      const {source, api, columnApi} = e;
      let previousColumnState = extractJsonPref(userPreferences, UserPreferences.jobGridColumnState)?.value;
      if (computedColumnsEnabled && previousColumnState) {
        previousColumnState = mapReadyDueColumnState(previousColumnState);
      }
      if (previousColumnState) {
        previousColumnState = addNewColumnStateAfter(previousColumnState, "jobAlias", "order.alias");
      }

      if (!ALLOWED_SAVED_PREFERENCE_SOURCES.includes(source)) {
        log.debug("reverted visible state");
        const revertVisibleState: ColumnState[] = getArrayFromField(previousColumnState).map(
          ({colId, hide}: ColumnState) => ({
            colId,
            hide
          })
        );
        columnApi.applyColumnState({
          state: revertVisibleState
        });
      }

      const newColumnState = columnApi.getColumnState();
      if (gridReady && !datasourceFailed && !isEqual(previousColumnState, newColumnState) && isInitialized) {
        setUserPref({
          variables: {
            name: UserPreferences.jobGridColumnState,
            input: createJsonPref(newColumnState, true)
          }
        })
          .then(() => {
            userPrefsQueryRefetch?.();
          })
          .catch((error) => log.debug(error));
        api.refreshServerSide();
      }
    },
    [
      userPreferences,
      computedColumnsEnabled,
      gridReady,
      datasourceFailed,
      isInitialized,
      setUserPref,
      userPrefsQueryRefetch
    ]
  );

  const onColumnMoveChanged = useCallback(
    (e: ColumnMovedEvent<Job>) => {
      if (!e.finished || e.source === "api") return;
      updateColumn(e);
    },
    [updateColumn]
  );

  const onColumnSortChanged = useCallback(
    (e: SortChangedEvent<Job>) => {
      if (e.source === "api") return;
      updateColumn(e);
    },
    [updateColumn]
  );

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent<Job>) => {
      if (e.source === "api") return;
      updateColumn(e);
    },
    [updateColumn]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent<Job>) => {
      setGridHasActiveFilters(Object.keys(event.api.getFilterModel()).length > 0);
      const savedFilterState = extractJsonPref(userPreferences, UserPreferences.jobGridFilterState)?.value;
      const newFilterState = event.api.getFilterModel();
      if (selectedJobGroups?.length === jobGroups.length) {
        newFilterState["dispatchGroup.name"] = {
          values: selectedJobGroups
        };
      }
      if (isInitialized && !isEqual(savedFilterState, newFilterState)) {
        try {
          setUserPref({
            variables: {
              name: UserPreferences.jobGridFilterState,
              input: createJsonPref(newFilterState, true)
            }
          }).then(() => {
            userPrefsQueryRefetch?.();
          });
        } catch (error) {
          log.debug(error);
        }
      }
      mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
    },
    [
      isInitialized,
      jobGroups.length,
      mapVisibilityDispatch,
      selectedJobGroups,
      setUserPref,
      userPreferences,
      userPrefsQueryRefetch
    ]
  );

  const onJobGroupSelected = (key: number) => {
    const name = jobGroupOptions.get(key)?.name;
    if (name && !selectedJobGroups?.includes(name)) {
      const newSelectedJobGroups = [...(selectedJobGroups || []), name];
      setSelectedJobGroups(newSelectedJobGroups);
      resetViewGestured();
    }
  };

  const onJobGroupUnselected = (key: number) => {
    const name = jobGroupOptions.get(key)?.name;
    if (name && selectedJobGroups?.includes(name)) {
      const newJobGroups = selectedJobGroups.filter((n) => n !== name);
      setSelectedJobGroups(newJobGroups.length > 0 ? newJobGroups : null);
      resetViewGestured();
    }
  };

  const onJobGroupUnselectedAll = () => {
    setSelectedJobGroups(null);
    resetViewGestured();
    gridRef?.current?.api.onFilterChanged();
  };

  const getColorIndicator = useCallback(
    async (job: AgJob) => {
      if (!ruleContextState.loading) {
        const colors: string[] = [];

        for (const stop of job.stops) {
          const engine = ruleContextState.getConfiguredEngine(
            {job: job, stop: stop},
            ruleContextState.rules as RuleProperties[]
          );
          if (engine === undefined) return;

          const results = await engine.run();

          if (results.events.length > 0) {
            colors.push(results.events[0].params?.color);
          }
        }

        job.indicatorColor = ruleContextState.colorRank
          ? getHighestRankedColor(colors, ruleContextState.colorRank)
          : undefined;
      }
    },
    [ruleContextState]
  );

  const buildIndicatorColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgJob[]) => {
      const promises = newRows.map(async (incomingRow) => {
        await getColorIndicator(incomingRow);
      });

      const results = await Promise.allSettled(promises);

      results.forEach((result) => {
        if (result.status === "rejected") {
          log.warn("Error getting color indicator", result.reason);
        }
      });
    },
    [getColorIndicator]
  );

  const sortAndFilterByColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgJob[], res: ApolloQueryResult<any>) => {
      if (filterAndSortByColorsEnabled) {
        const {sortModel, filterModel} = params.request;
        const filterByColors = filterModel?.indicatorColor;
        const sortByColors = sortModel.find((item) => item.colId === "indicatorColor");
        if (filterByColors) {
          filterByColors.values[filterByColors.values.findIndex((item: string) => item === "No Color")] = undefined;
          newRows.forEach((row, index) => {
            if (!filterByColors.values.includes(row.indicatorColor)) {
              (newRows[index] as ExtendedAgJob).hide = true;
            }
          });
        }
        if (sortByColors) {
          const sortDirection = sortByColors.sort === "asc" ? 1 : -1;
          newRows.sort((a, b) => {
            return (
              (getColorPriority(ruleContextState.rules, a.indicatorColor) -
                getColorPriority(ruleContextState.rules, b.indicatorColor)) *
              sortDirection
            );
          });
        }
        const hideRows = newRows.filter((row) => (row as ExtendedAgJob).hide);

        hideRows.forEach((hideRow) => {
          const hideRowIndex = newRows.findIndex((row) => row.jobId === hideRow.jobId);
          newRows.splice(hideRowIndex, 1);
          newRows.push(hideRow);
        });

        if (res.data.searchJobs.total > 100) {
          const warningMsg = getWarningMessage(Boolean(sortByColors), Boolean(filterByColors));
          setWarningMessage(warningMsg);
        } else {
          setWarningMessage(undefined);
        }
      }
    },
    [filterAndSortByColorsEnabled, ruleContextState.rules]
  );

  const transferActiveMapVisibilityToIncoming = async (
    params: IServerSideGetRowsParams,
    newRows: AgJob[]
  ): Promise<void> => {
    const currentJobRowsWithMapVisibilityObject: AgJob[] = [];
    params.api.forEachNode(({data: row}) => {
      if (row?.map) {
        currentJobRowsWithMapVisibilityObject.push(row);
      }
    });

    // if any of the incoming rows intersect with current rows that have a visibility object...
    if (
      currentJobRowsWithMapVisibilityObject.length > 0 &&
      newRows.some((res) =>
        currentJobRowsWithMapVisibilityObject.map((incomingRow) => incomingRow.jobId).includes(res.jobId)
      )
    ) {
      // transfer their existing map visibility objects to their new row
      newRows.forEach((incomingRow) => {
        const index = currentJobRowsWithMapVisibilityObject.findIndex(
          (currentRowWithVisibilityObject) => currentRowWithVisibilityObject.jobId === incomingRow.jobId
        );
        if (index > -1) {
          incomingRow.map = currentJobRowsWithMapVisibilityObject[index].map;
        }
      });
    }
  };

  const onRestoreDefaults = useCallback(
    (justRestoreFilters?: boolean) => {
      gridRef.current?.api.setFilterModel(null);
      setSelectedReadyDueFilterOption(undefined);
      setSelectedPlacedInLastFilterOption(undefined);
      setSelectedJobGroups(null);
      resetViewGestured();
      const promises = [
        setUserPref({variables: {name: UserPreferences.jobGridFilterReadyDue, input: createJsonPref(null, true)}}),
        setUserPref({variables: {name: UserPreferences.jobGridFilterPlaced, input: createJsonPref(null, true)}}),
        setUserPref({variables: {name: UserPreferences.jobGridFilterState, input: createJsonPref(null, true)}})
      ];
      if (!justRestoreFilters) {
        promises.push(
          setUserPref({variables: {name: UserPreferences.jobGridColumnState, input: createJsonPref(null, true)}})
        );
      }
      Promise.all(promises).then(() => {
        userPrefsQueryRefetch?.();
      });

      if (!justRestoreFilters) {
        gridRef.current?.columnApi.resetColumnState();
      }

      gridRef.current?.api.onFilterChanged();
    },
    [resetViewGestured, setUserPref, userPrefsQueryRefetch]
  );

  const handleGridReady = useCallback(
    (e) => {
      setGridReady(true);
      if (gridRef.current) {
        getGridRef?.(gridRef.current);
      }
      // DP-1305
      // assure that any older saved grid state, where the map column had been previously hidden, is restored to visibility
      e.columnApi.setColumnsVisible(["map"], true);
    },
    [getGridRef]
  );

  const onCellClicked = useCallback(() => {
    if (gridRef.current?.api.isToolPanelShowing()) {
      gridRef.current?.api.closeToolPanel();
    }
  }, []);

  const isSuggestionMode =
    suggestedAssignment.isLoading || suggestedAssignment.isActive || suggestedAssignment.state === "complete";

  const handleSelectedRow = useMemo(
    () =>
      debounce((event: RowSelectedEvent<Job>) => {
        if (isSuggestionMode) {
          return;
        }
        const pendingJobIds = assignmentState.pendingActions.flatMap((pa) => pa.jobIds);
        const selectedNodes = event.api.getSelectedNodes().filter((node) => !pendingJobIds.includes(node.data!.jobId));
        assignmentDispatch({type: "SetSelectedJobs", payload: selectedNodes.map((node) => node.data!)});
        assignmentViewDispatch({
          type: AssignmentViewActionTypes.SET_JOBS_NUMBER_ON_ALL_ASSIGNMENTS,
          payload: event.data?.jobNumber as string
        });
      }, 50),
    [assignmentDispatch, assignmentState.pendingActions, assignmentViewDispatch, isSuggestionMode]
  );

  const rowDragText = useCallback((params: IRowDragItem, dragItemCount: number) => {
    return dragItemCount === 1
      ? `Job #${params.rowNode?.data.jobNumber}`
      : `${gridRef.current?.api.getSelectedRows().length} Jobs`;
  }, []);

  const onRowDragEnter = useCallback((e: RowDragEnterEvent) => {
    e.api.setEnableCellTextSelection(false);
    document.getElementsByTagName("body")[0].style["userSelect"] = "none";
  }, []);

  const onRowDragEnd = useCallback((e: RowDragEndEvent) => {
    e.api.setEnableCellTextSelection(true);
    document.getElementsByTagName("body")[0].style["userSelect"] = "";
  }, []);

  const openOrCloseToolPanel = (key: string) => {
    if (gridReady) {
      if (gridRef.current!.api.getOpenedToolPanel() === key) {
        gridRef.current!.api.closeToolPanel();
      } else {
        gridRef.current!.api.openToolPanel(key);
      }
    }
  };

  const clearTimeFilter = () => {
    if (selectedReadyDueFilterOption) {
      clearTimeFilters();
      setUserPref({
        variables: {
          name: UserPreferences.jobGridFilterReadyDue,
          input: createJsonPref(null, true)
        }
      })
        .then(() => {
          userPrefsQueryRefetch?.();
        })
        .catch(log.debug);
    }
  };

  const clearPlacedInLastTime = () => {
    if (selectedPlacedInLastFilterOption) {
      gridRef.current!.api.getFilterInstance("order.createdDateTime", (filter) => {
        setSelectedPlacedInLastFilterOption(undefined);
        filter?.setModel(null);
      });
      setUserPref({
        variables: {
          name: UserPreferences.jobGridFilterPlaced,
          input: createJsonPref(null, true)
        }
      })
        .then(() => {
          userPrefsQueryRefetch?.();
        })
        .catch(log.debug);
    }
  };

  const clearTimeFilters = () => {
    setSelectedReadyDueFilterOption(undefined);
    gridRef.current!.api.getFilterInstance("stop.pickup.scheduledDateTime", (filter) => {
      filter?.setModel(null);
    });
    gridRef.current!.api.getFilterInstance("stop.delivery.scheduledDateTime", (filter) => {
      filter?.setModel(null);
    });
  };

  const handleGlobalSearch = (query: string) => {
    if (gridContext.globalQuery != query) {
      const newGridContext = {...gridContext, globalQuery: query};
      if (!isEqual(newGridContext, gridContext)) {
        setGridContext(newGridContext);
        resetViewGestured();
      }
    }
  };

  const handleAssignJobs = (dragSelectedJobs?: Job[], selectedManifestByDrop?: Manifest) => {
    let selectedManifest;
    if (selectedManifestByDrop) {
      selectedManifest = [selectedManifestByDrop];
    } else if (assignmentState.selectedManifest) {
      selectedManifest = [assignmentState.selectedManifest];
    } else {
      selectedManifest = undefined;
    }
    const selectedJobs =
      dragSelectedJobs && dragSelectedJobs.length > 0 && dragSelectedJobs[0].jobId
        ? dragSelectedJobs
        : assignmentState.selectedJobs;

    const isMatchRequirementsManifest =
      getMatchRequirementManifests({
        selectedRows: selectedJobs,
        manifests: selectedManifest,
        isSkillsBasedRequirements: isSkillsBased,
        activeSkills: skills
      }).length === 1;

    const isRequirementsEmpty =
      selectedJobs.filter((job) => (isSkillsBased ? job?.order?.orderSkills : job?.order?.requirements)).length === 0;

    const isDragWithoutSelection =
      !!dragSelectedJobs && dragSelectedJobs.length > 0 && assignmentState.selectedJobs.length === 0;
    const doAssign =
      isDragWithoutSelection && selectedManifestByDrop
        ? () => assignDirect(selectedJobs, selectedManifestByDrop as TManifestEnhanced)
        : assign;

    if (
      isMatchRequirementsManifest ||
      isRequirementsEmpty ||
      driverRequiredSetting === DriverRequirementEnforcementLevel.Allow
    ) {
      checkEverCompletedBeforeAssign(selectedJobs, doAssign);
    } else {
      if (isDragWithoutSelection && selectedManifestByDrop) {
        assignmentDispatch({type: "SetSelectedManifest", payload: selectedManifestByDrop as TManifestEnhanced});
        assignmentDispatch({type: "SetSelectedJobs", payload: selectedJobs});
      }
      pendingConfirmActionRef.current = () => checkEverCompletedBeforeAssign(selectedJobs, doAssign);
      setShowAssignJobsConfirm(true);
    }
  };

  const handleAssignJobsConfirm = (reasons: Record<number, string>) => {
    setShowAssignJobsConfirm(false);

    skillOverrideNotesRef.current = buildSkillOverrideNotes({
      jobs: assignmentState.selectedJobs,
      reasons,
      driverSkills: assignmentState.selectedManifest?.driver?.skills,
      activeSkills: skills
    });

    const action = pendingConfirmActionRef.current;
    pendingConfirmActionRef.current = null;
    if (action) {
      action();
    } else {
      checkEverCompletedBeforeAssign(assignmentState.selectedJobs, assign);
    }
  };

  const assign = () => {
    if (suggestedAssignment.isAssigning) return;
    if (suggestedAssignment.isActive && assignmentState.selectedManifest) {
      const candidate = suggestedAssignment.candidates.find(
        (c) => c.manifestDriverId === assignmentState.selectedManifest?.manifestDriverId
      );
      if (candidate) {
        const completedStopIds = (assignmentState.selectedManifest.stops ?? [])
          .filter(isCompleted)
          .sort((a, b) => a.manifestSequence - b.manifestSequence)
          .map((s) => s.jobStopId);
        const jobs = assignmentState.selectedJobs;
        const manifest = assignmentState.selectedManifest;
        const overrideNotes = skillOverrideNotesRef.current;
        skillOverrideNotesRef.current = [];
        suggestedAssignment
          .confirmCandidate(candidate, completedStopIds, overrideNotes.length ? overrideNotes : undefined)
          .then((success) => {
            if (success) {
              const pendingAction: PendingAssignment = {
                jobIds: jobs.map((j) => j.jobId),
                jobStopIds: [],
                manifestDriverId: candidate.manifestDriverId,
                user: "",
                assignmentDateTime: Date.now(),
                status: JobAssignmentSubmissionResult.PENDING,
                updatedAt: Date.now()
              };
              assignmentDispatch({type: "ClearSelectionAndSetPendingAction", payload: pendingAction});
              showAssignmentToast(jobs, manifest, authState, oktaAuth.getAccessToken() ?? "");
            } else {
              AppToaster.show({
                intent: Intent.WARNING,
                icon: "warning-sign",
                message: `Error assigning job id(s): ${jobs.map((j) => j.jobId).join(", ")}`
              });
            }
          })
          .finally(() => {
            suggestedAssignment.cancel();
          });
        return;
      }
    }
    const overrideNotes = skillOverrideNotesRef.current;
    skillOverrideNotesRef.current = [];
    assignmentDispatch({
      type: "AssignJobsToManifest",
      payload: {
        assignFunction: assignJobsToManifest,
        authState: authState,
        authToken: oktaAuth.getAccessToken() ?? "",
        ...(overrideNotes.length ? {skillOverrideNotes: overrideNotes} : {})
      }
    });
    clearSelection();
  };

  const assignDirect = (jobs: Job[], manifest: TManifestEnhanced) => {
    const overrideNotes = skillOverrideNotesRef.current;
    skillOverrideNotesRef.current = [];
    assignmentDispatch({
      type: "AssignJobsToManifest",
      payload: {
        assignFunction: assignJobsToManifest,
        authState: authState,
        authToken: oktaAuth.getAccessToken() ?? "",
        isFromGlobalFind: true,
        selectedJobs: jobs,
        selectedManifest: manifest,
        ...(overrideNotes.length ? {skillOverrideNotes: overrideNotes} : {})
      }
    });
    clearSelection();
  };

  const checkEverCompletedBeforeAssign = useCallback(
    (jobs: Job[], onPass: () => void) => {
      const jobInfoMap = new Map(
        jobs.map((job) => [job.jobId, {jobNumber: job.jobNumber, orderId: job.order?.orderId}])
      );

      getEverCompletedJobs(
        jobs.map((job) => job.jobId),
        jobInfoMap
      )
        .then((result) => {
          if (!result || result.length === 0) {
            onPass();
          } else {
            setEverCompletedJobs(result);
            setShowEverCompletedDialog(true);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    },
    [getEverCompletedJobs]
  );

  const clearSelection = () => {
    if (gridReady) {
      if (suggestedAssignment.isActive || suggestedAssignment.isLoading || suggestedAssignment.state === "complete") {
        suggestedAssignment.cancel();
      }
      assignmentDispatch({type: "ClearSelections"});
      gridRef.current!.api.deselectAll();
      if (driverRequirementsEnabled) {
        setIsEnableQualified(false);
      }
    }
  };

  const toggleQualifiedDriversHandler = () => {
    setIsEnableQualified((prev) => !prev);
  };

  useEffect(() => {
    if (!driverRequirementsEnabled) return;
    const debouncedDispatch = debounce(() => {
      if (!isEnableQualified) {
        assignmentDispatch({type: "SetJobsRequirements", payload: new Map<number, JobRequirement>()});
      } else {
        const driverRequirementsIds = new Map<number, JobRequirement>();
        assignmentState.selectedJobs.forEach((job) => {
          if (isSkillsBased) {
            const activeSkillIds = skills.map((skill) => skill.id);
            job.order?.orderSkills?.forEach((skill) => {
              if (activeSkillIds.includes(skill.id)) {
                driverRequirementsIds.set(skill.id, {id: skill.id, name: skill.name} as JobRequirement);
              }
            });
          } else {
            job.order?.requirements?.forEach((requirement) => {
              driverRequirementsIds.set(requirement.id, {id: requirement.id, name: requirement.name} as JobRequirement);
            });
          }
        });
        assignmentDispatch({
          type: "SetJobsRequirements",
          payload: driverRequirementsIds
        });
      }
    }, 500);

    debouncedDispatch();

    return () => {
      debouncedDispatch.cancel();
    };
  }, [
    assignmentDispatch,
    assignmentState.selectedJobs,
    isEnableQualified,
    driverRequirementsEnabled,
    skills,
    isSkillsBased
  ]);

  const onPaginationChanged = useCallback(
    (event: PaginationChangedEvent<Job>) => {
      const currentPage = event.api.paginationGetCurrentPage() + 1;
      const pageSize = event.api.paginationGetPageSize();
      const startRow = (currentPage - 1) * pageSize;
      const endRow = currentPage * pageSize - 1;
      const jobIds: number[] = [];
      const jobIdsAndJobNumbers: {jobId: number; jobNumber: string}[] = [];
      event.api.forEachNode((rowNode) => {
        if (
          rowNode.data?.jobId &&
          isNumber(rowNode.rowIndex) &&
          rowNode.rowIndex >= startRow &&
          rowNode.rowIndex <= endRow &&
          !(rowNode.data as ExtendedAgJob).hide
        ) {
          jobIds.push(rowNode.data.jobId);
        }
        if (rowNode.data) {
          jobIdsAndJobNumbers.push({jobId: rowNode.data.jobId, jobNumber: rowNode.data.jobNumber});
        }
      });

      assignmentViewDispatch({
        type: AssignmentViewActionTypes.SET_SHOWING_JOBIDS,
        payload: {
          jobIds,
          total: event.api.paginationGetRowCount(),
          jobIdsAndJobNumbers: jobIdsAndJobNumbers
        }
      });

      const firstRowOnPage = event.api.getDisplayedRowAtIndex(startRow);
      if (firstRowOnPage?.failedLoad) {
        event.api.showNoRowsOverlay();
      } else if (firstRowOnPage?.data) {
        event.api.hideOverlay();
      }

      if (currentPage !== currentJobPanePage) {
        mapVisibilityDispatch({type: "SetIsViewGestured", payload: false});
        setCurrentJobPanePage(currentPage);
      }
    },
    [assignmentViewDispatch, currentJobPanePage, mapVisibilityDispatch]
  );

  const onFirstDataRendered = useCallback(() => {
    mapVisibilityDispatch({
      type: "SetJobPanelLoaded",
      isLoaded: true
    });
  }, [mapVisibilityDispatch]);

  const onRemoveFilters = useCallback(() => {
    onRestoreDefaults(true);
  }, [onRestoreDefaults]);

  const openOrderEntry = useCallback(async () => {
    setIsOpeningCMSOrder(true);
    await navigateToCmsOrder(authState.tenant!, oktaAuth.getAccessToken() ?? "", undefined, tenantPreferences);
    setIsOpeningCMSOrder(false);
  }, [authState.tenant, tenantPreferences, oktaAuth]);

  const colorColDefs: ColDef[] = useMemo(() => {
    return filterAndSortByColorsEnabled
      ? [
          {
            field: "indicatorColor",
            headerName: "Color",
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            sortable: true,
            rowDrag: true,
            resizable: false,
            width: 60,
            cellRenderer: ColorIndicatorCellRenderer,
            type: "filterColumn",
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: ColorCellRenderer,
              values: [
                NO_COLOR_FILTER_VALUE,
                ...(ruleContextState.rules ?? []).map((rule) => rule.event.params?.color)
              ],
              suppressMiniFilter: true
            },
            cellClass: "drag-column"
          }
        ]
      : [
          {
            field: "indicatorColor",
            headerName: "Color",
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            suppressMovable: true,
            rowDrag: false,
            resizable: false,
            width: 18,
            minWidth: 18,
            cellRenderer: ColorIndicatorCellRenderer,
            headerComponent: () => ""
          },
          {
            field: "",
            headerName: "",
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            suppressMovable: true,
            resizable: false,
            rowDrag: true,
            cellStyle: {justifyContent: "center", alignItems: "center", display: "flex"},
            width: 18,
            minWidth: 18,
            suppressColumnsToolPanel: true,
            cellClass: "drag-column"
          }
        ];
  }, [filterAndSortByColorsEnabled, ruleContextState.rules]);

  const computedColumnsDef = useMemo(
    () =>
      computedColumnsEnabled
        ? [
            {
              colId: "readyAt",
              field: "readyAt",
              headerName: "Ready At",
              type: "filterColumn",
              sortable: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: params.data.readyAt,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(params.data.readyAt, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "dueBy",
              field: "dueBy",
              headerName: "Due By",
              type: "filterColumn",
              sortable: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: params.data.dueBy,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(params.data.dueBy, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.pickup.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Ready_Hidden",
              suppressColumnsToolPanel: true,
              type: "filterColumn",
              hide: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.delivery.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Due By_Hidden",
              suppressColumnsToolPanel: true,
              type: "filterColumn",
              hide: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;
                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            }
          ]
        : [
            {
              colId: "stop.pickup.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Ready",
              type: "filterColumn",
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.delivery.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Due By",
              type: "filterColumn",
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            }
          ],
    [appState.isTimeFormat24hr, computedColumnsEnabled, timezoneOfJobStop]
  );

  const skillsColumnDef: ColDef[] = useMemo(
    () =>
      skillsEnabled
        ? [
            {
              colId: "order.orderSkills.id",
              field: "order.orderSkills.id",
              headerName: "Skills",
              initialHide: true,
              type: "filterColumn",
              filter: "agSetColumnFilter",
              filterParams: getOrderSkillFilterParams(skills),
              valueGetter: (params: any) => {
                return orderSkillsValueGetter(params, skills);
              },
              cellRenderer: CellRendererWithToolTip
            } as ColDef
          ]
        : [],
    [skills, skillsEnabled]
  );

  const columnDefs: ColDef[] = useMemo(
    () => {
      return [
        ...colorColDefs,
        {
          field: "jobNumber",
          headerName: "Job #",
          pinned: "left",
          lockPosition: "left",
          sortable: true,
          type: ["filterColumn", "textFilter"],
          rowDrag: false,
          cellClass: "job-number-cell",
          minWidth: 50,
          cellRenderer: JobNumberCellRenderer,
          valueGetter: (params) => {
            return {
              testIdPrefix: "more-details-button",
              text: params.data.jobNumber,
              minimal: true,
              intent: Intent.PRIMARY,
              onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, data: any, authState: AuthState) => {
                gridRef.current?.api.showLoadingOverlay();
                navigateToCmsOrder(
                  authState.tenant!,
                  oktaAuth.getAccessToken() ?? "",
                  data.order.orderId,
                  tenantPreferences
                ).finally(() => gridRef.current?.api.hideOverlay());
              },
              dispatchNotesEnabled: dispatchNotesEnabled
            };
          }
        },
        ...computedColumnsDef,
        {
          colId: "order.customer.name",
          field: "order.customer.name",
          headerName: "Customer Account",
          type: "filterColumn",
          sortable: true,
          filter: CustomerFilter
        },
        {
          colId: "stop.pickup.dispatchZone",
          field: "stops.dispatchZone,stops.stopType",
          headerName: "Pickup Zone",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.dispatchZone,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.dispatchZone",
          field: "stops.dispatchZone,stops.stopType",
          headerName: "Delivery Zone",
          sortable: true,
          valueGetter: (params) => deliveryStop(params.data.stops)?.dispatchZone,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "service",
          headerName: "SVC Type",
          cellRenderer: serviceTypeCellRenderer,
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            cellRenderer: serviceTypeCellRenderer,
            values: services
          }
        },
        {
          field: "vehicleType.description",
          headerName: "Vehicle",
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: vehicleTypes
          }
        },
        {
          colId: "stop.pickup.address",
          field: "stops.address,stops.stopType",
          headerName: "Pickup Address",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.address,
          type: ["filterColumn", "textFilter"],
          cellStyle: {
            cursor: "pointer"
          },
          cellClass: "unassigned-jobs-pickup-address",
          onCellClicked: (params: CellClickedEvent<Job>) => {
            if (!params.data) return;
            const address = pickupStop(params.data.stops)?.address ?? "";
            copyToClipboard(address).then(() => {
              /*Noop*/
            });
          }
        },
        {
          colId: "stop.delivery.address",
          field: "stops.address,stops.stopType",
          headerName: "Delivery Address",
          valueGetter: (params) => deliveryStop(params.data.stops)?.address,
          type: ["filterColumn", "textFilter"],
          cellStyle: {
            cursor: "pointer"
          },
          cellClass: "unassigned-jobs-delivery-address",
          onCellClicked: (params: CellClickedEvent<Job>) => {
            if (!params.data) return;
            const address = deliveryStop(params.data.stops)?.address ?? "";
            copyToClipboard(address).then(() => {
              /*Noop*/
            });
          }
        },
        {field: "pieces", headerName: "Pieces", sortable: true, type: ["filterColumn", "numberFilter"], maxWidth: 67},
        {field: "weight", headerName: "Weight", sortable: true, type: ["filterColumn", "numberFilter"], maxWidth: 70},
        {field: "order.notes", headerName: "Order Notes", type: ["filterColumn", "textFilter"]},

        //------------DESELECT FIELDS------------//
        {
          field: "stops.dispatchZone",
          headerName: "Zone",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            return {
              upperField: {
                value: pickupStop(params.data.stops)?.dispatchZone,
                tooltip: `Pickup: ${pickupStop(params.data.stops)?.dispatchZone}`
              },
              lowerField: {
                value: deliveryStop(params.data.stops)?.dispatchZone,
                tooltip: `Delivery: ${deliveryStop(params.data.stops)?.dispatchZone}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "time",
          headerName: "Time",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            const pickupScheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
            const pickupTimeZone = pickupStop(params.data.stops)?.timeZone;
            const deliveryScheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
            const deliveryTimeZone = deliveryStop(params.data.stops)?.timeZone;

            const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
            const getDateFnc = timezoneOfJobStop ? getDateTimeAtSpecificTimeZone : getLocalDateTime;

            return {
              upperField: {
                value: getTimeFnc({
                  dateStr: pickupScheduledDateTime,
                  timeZone: pickupTimeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                }),
                tooltip: `Pickup: ${getDateFnc({
                  dateStr: pickupStop(params.data.stops)?.scheduledDateTime,
                  timeZone: pickupStop(params.data.stops)?.timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr
                })}`
              },
              lowerField: {
                value: getTimeFnc({
                  dateStr: deliveryScheduledDateTime,
                  timeZone: deliveryTimeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                }),
                tooltip: `Delivery: ${getDateFnc({
                  dateStr: deliveryStop(params.data.stops)?.scheduledDateTime,
                  timeZone: deliveryStop(params.data.stops)?.timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr
                })}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "stops.address",
          headerName: "Address",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            return {
              upperField: {
                value: pickupStop(params.data.stops)?.address,
                tooltip: `Pickup: ${pickupStop(params.data.stops)?.address}`
              },
              lowerField: {
                value: deliveryStop(params.data.stops)?.address,
                tooltip: `Delivery: ${deliveryStop(params.data.stops)?.address}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "dispatchGroup.name",
          headerName: "Job Group",
          hide: true,
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: jobGroups.map((d) => d.dispatchGroup!)
          }
        },
        {
          field: "jobStatus",
          headerName: "Status",
          sortable: true,
          hide: true,
          suppressColumnsToolPanel: true,
          cellRenderer: JobStatusCellRenderer,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            cellRenderer: JobStatusCellRenderer,
            values: (params: SetFilterValuesFuncParams) => {
              getFilterValues(params, getDistinctValues, "jobs", "jobStatus");
            }
          }
        },
        {field: "order.caller", headerName: "Caller", hide: true, type: ["filterColumn", "textFilter"], sortable: true},
        {
          field: "order.callerPhone",
          headerName: "Phone",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.alias",
          headerName: "Customer Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.customerCode",
          headerName: "Account #",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.notes",
          headerName: "Customer Notes",
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "driver.driverCode",
          headerName: "Driver Code",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {field: "order.liftGateRequired", headerName: "Lift Gate", hide: true /*TODO: BOOLEAN Filter*/},
        {
          field: "site.code",
          headerName: "Site",
          sortable: true,
          hide: true,
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            values: sites
          }
        },
        {
          field: "order.alias",
          headerName: "Order Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "jobAlias",
          headerName: "Job Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.auth",
          headerName: "Order Auth",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.orderId",
          headerName: "Order Id",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.codAmount",
          headerName: "COD Amount",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.description",
          headerName: "Order Description",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.priority.description",
          headerName: "Order Priority",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "routeNumber",
          headerName: "Route#",
          sortable: true,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "order.totalMiles",
          headerName: "Total Mileage",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.origin",
          headerName: "Origin",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.packageType.description",
          headerName: "Package Type",
          hide: true,
          type: ["filterColumn"],
          sortable: true,
          filter: "agSetColumnFilter",
          filterParams: {
            values: packageTypes
          }
        },
        {
          field: "order.qualityFlag",
          headerName: "Quality Flag",
          hide: true,
          sortable: true /*TODO: BOOLEAN Filter*/
        },
        {
          colId: "stop.pickup.name",
          field: "stops.name,stops.stopType",
          headerName: "Pickup Name",
          valueGetter: (params) => pickupStop(params.data.stops)?.name,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.city",
          field: "stops.city,stops.stopType",
          headerName: "Pickup City",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.city,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.state",
          field: "stops.state,stops.stopType",
          headerName: "Pickup State",
          valueGetter: (params) => pickupStop(params.data.stops)?.state,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.note",
          field: "stops.note,stops.stopType",
          headerName: "Pickup Notes",
          valueGetter: (params) => pickupStop(params.data.stops)?.note,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.zip",
          field: "stops.zip,stops.stopType",
          headerName: "Pickup Zip Code",
          valueGetter: (params) => pickupStop(params.data.stops)?.zip,
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          colId: "stop.delivery.name",
          field: "stops.name,stops.stopType",
          headerName: "Delivery Name",
          valueGetter: (params) => deliveryStop(params.data.stops)?.name,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.city",
          field: "stops.city,stops.stopType",
          headerName: "Delivery City",
          sortable: true,
          valueGetter: (params) => deliveryStop(params.data.stops)?.city,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.state",
          field: "stops.state,stops.stopType",
          headerName: "Delivery State",
          valueGetter: (params) => deliveryStop(params.data.stops)?.state,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.note",
          field: "stops.note,stops.stopType",
          headerName: "Delivery Notes",
          valueGetter: (params) => deliveryStop(params.data.stops)?.note,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.zip",
          field: "stops.zip,stops.stopType",
          headerName: "Delivery Zip Code",
          valueGetter: (params) => deliveryStop(params.data.stops)?.zip,
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "stopCount",
          headerName: "Number of Stops",
          sortable: true,
          type: ["filterColumn", "numberFilter"],
          hide: true
        },
        {
          field: "order.requirements.name",
          headerName: "Order Requirements",
          valueGetter: (params) => params.data.order?.requirements?.map((or: OrderRequirement) => or.name).join(", "),
          hide: true,
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            values: [AG_GRID_NOT_EXIST_FILTER, ...orderRequirements],
            valueFormatter: (params: any) => {
              if (params.value === AG_GRID_NOT_EXIST_FILTER) {
                return "No Requirements";
              }
              return params.value;
            }
          }
        },
        {
          field: "order.createdDateTime",
          hide: true,
          sortable: true,
          headerName: "Order Placed",
          type: "filterColumn",
          filter: DateFilter,
          filterParams: {
            type: "range",
            dateRangeShortcuts: [
              {label: "Today", dateRange: [new Date(), new Date()]},
              {label: "Yesterday", dateRange: [subDays(new Date(), 1), subDays(new Date(), 1)]},
              {label: "Last 7 days", dateRange: [subDays(new Date(), 6), new Date()]},
              {label: "Last 14 days", dateRange: [subDays(new Date(), 13), new Date()]}
            ]
          },
          cellRenderer: doubleFieldCellRenderer,
          valueGetter: (params) => {
            return {
              upperField: {
                value: getLocalTime({
                  dateStr: params.data.order.createdDateTime,
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                })
              },
              lowerField: {
                value: getLocalDate(
                  params.data.order.createdDateTime,
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                  "EEE MMM dd yyyy"
                )
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "order.declaredValue",
          headerName: "Declared Value",
          sortable: true,
          type: ["filterColumn", "numberFilter"],
          initialHide: true
        },
        {
          field: "order.user.userName",
          headerName: "User",
          initialHide: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: users
          },
          sortable: true
        },
        ...skillsColumnDef,
        {
          field: "map",
          hide: false, // map column to remain visible at all times
          headerName: "Map",
          headerComponent: "div",
          suppressColumnsToolPanel: true,
          pinned: "right",
          lockPinned: true,
          resizable: false,
          maxWidth: 35,
          headerClass: "map-icon-header",
          cellClass: "map-icon-cell",
          cellStyle: {display: "flex", alignItems: "center", justifyContent: "center", padding: "2px"},
          cellRenderer: MapIconCellRenderer,
          valueGetter: (params) => params.data.jobId
        }
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      colorColDefs,
      computedColumnsDef,
      skillsColumnDef,
      oktaAuth,
      services,
      vehicleTypes,
      packageTypes,
      users,
      dispatchNotesEnabled
    ]
  ) as ColDef[];

  const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressColumnSelectAll: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        },
        {
          id: "settings",
          labelDefault: "Settings",
          labelKey: "settings",
          iconKey: "menu",
          toolPanel: GridSettingsToolPanel,
          toolPanelParams: {
            onRestoreDefaults: onRestoreDefaults
          }
        }
      ],
      defaultToolPanel: "customStats"
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFilter: IActiveFilterDialogProps = {
    message:
      "To view all unassigned jobs, please clear any active filters in the table header that may be limiting your job list.",
    title: "Active Filters Present",
    icon: <Icon icon="filter" color="green" size={21} />,
    actionButton: {
      label: "Remove Filter(s)",
      action: onRemoveFilters
    }
  };

  // Initialize grid state from user preferences once all data is loaded
  useEffect(() => {
    if (
      !isInitialized &&
      gridReady &&
      isUserPreferencesLoaded &&
      isDispatchGroupLoaded &&
      isDispatchDataLoaded &&
      !ruleContextState.loading
    ) {
      log.debug("All data loaded, initializing grid");
      let savedColumnState = extractJsonPref(userPreferences, UserPreferences.jobGridColumnState).value;
      if (savedColumnState) {
        if (computedColumnsEnabled) {
          savedColumnState = mapReadyDueColumnState(savedColumnState);
        }
        savedColumnState = addNewColumnStateAfter(savedColumnState, "jobAlias", "order.alias");
        gridRef.current!.columnApi.applyColumnState({state: savedColumnState, applyOrder: true});
      }

      const savedFilterState = extractJsonPref(userPreferences, UserPreferences.jobGridFilterState).value;
      const hasFilterState = savedFilterState && Object.keys(savedFilterState).length > 0;
      if (hasFilterState) {
        gridRef.current!.api.setFilterModel(savedFilterState);
        if (Object.keys(savedFilterState).includes("dispatchGroup.name")) {
          setSelectedJobGroups(savedFilterState["dispatchGroup.name"].values);
        }
      }

      const savedReadyDueFilterCustomValue = extractJsonPref(
        userPreferences,
        UserPreferences.jobGridFilterReadyDueCustomValue,
        DefaultTimeFilterCustomValue
      ).value;
      if (savedReadyDueFilterCustomValue) {
        Object.keys(savedReadyDueFilterCustomValue).forEach((key) => {
          const customOptionIndex = ReadyDueFilterOptions.findIndex((o) => isCustomOption(o) && o.type === key);
          if (customOptionIndex > -1) {
            ReadyDueFilterOptions[customOptionIndex].customValue = savedReadyDueFilterCustomValue[key];
          }
        });
      }

      const savedReadyDueFilter = extractJsonPref(userPreferences, UserPreferences.jobGridFilterReadyDue).value;
      if (savedReadyDueFilter) {
        const title = savedReadyDueFilter.title;
        const type = savedReadyDueFilter.type;
        const option = ReadyDueFilterOptions.find((o) => o.title === title && o.type === type);
        if (option) {
          onConfirmSelectedTimeFilter(option);
        }
      }

      const savedPlacedInLastFilter = extractJsonPref(userPreferences, UserPreferences.jobGridFilterPlaced).value;
      if (savedPlacedInLastFilter) {
        const option = PlacedInLastFilterOptions.find((o) => o.title === savedPlacedInLastFilter);
        if (option) {
          handleSelectedPlacedInLastTime(option);
        }
      }

      const hasFilterJob = hasFilterState | savedReadyDueFilter | savedPlacedInLastFilter;
      if (hasFilterJob) {
        jobDialogRefs.current?.onOpen();
      }
      setInitialized(true);
    }
  }, [
    computedColumnsEnabled,
    gridReady,
    handleSelectedPlacedInLastTime,
    isDispatchDataLoaded,
    isDispatchGroupLoaded,
    isInitialized,
    isUserPreferencesLoaded,
    onConfirmSelectedTimeFilter,
    ruleContextState.loading,
    userPreferences
  ]);

  // Re-assert the hidden invariant when computedColumns resolves after grid init (Root Cause A fix)
  useEffect(() => {
    if (!gridReady || !computedColumnsEnabled) return;
    gridRef.current?.columnApi.applyColumnState({
      state: LEGACY_READY_DUE_COL_IDS.map((colId) => ({colId, hide: true}))
    });
  }, [gridReady, computedColumnsEnabled]);

  // Set up data source for job grid
  useEffect(() => {
    if (gridReady && isInitialized) {
      setDatasource(
        createJobsServerSideDatasource({
          client: apolloClient,
          defaultFields: [
            "jobId",
            "jobNumber",
            "readyAt",
            "dueBy",
            "service",
            "order.orderId",
            "stops.stopType",
            "stops.jobStopId",
            "stops.sequence",
            "stops.manifestSequence",
            "stops.scheduledDateTime",
            "stops.address",
            "stops.city",
            "stops.state",
            "stops.zip",
            "stops.timeZone",
            "stops.note",
            "vehicleType.vehicleTypeId",
            "order.customer.customerId",
            "order.priority.orderPriorityId",
            "order.priority.description",
            "order.requirements.name",
            "order.requirements.id",
            "order.orderSkills.name",
            "order.orderSkills.id",
            "site.siteId",
            "order.orderEvents.eventDateTime",
            "order.orderEvents.eventType",
            "order.orderEvents.note",
            "order.orderEvents.orderEventId",
            "order.orderEvents.userId",
            "order.orderEvents.userName"
          ],
          excludeFields: ["map", ...(backendColorRulesEnabled ? [] : ["indicatorColor"]), "time"],
          onDatasourceFail: datasourceFailCallback,
          decorateResults: [
            ...(backendColorRulesEnabled ? [] : [buildIndicatorColors, sortAndFilterByColors]),
            transferActiveMapVisibilityToIncoming
          ],
          tokenExpiry: authState.expiresAt ? new Date(authState.expiresAt) : undefined,
          backendColorRules: backendColorRulesEnabled
        })
      );
    }
  }, [
    apolloClient,
    buildIndicatorColors,
    datasourceFailCallback,
    assignmentViewDispatch,
    sortAndFilterByColors,
    authState.expiresAt,
    gridReady,
    isInitialized
  ]);

  // Set up drag and drop targets for manifests
  useEffect(() => {
    if (gridReady && isInitialized) {
      //remove existing drop zones
      if (currentDragAndDropTargets) {
        currentDragAndDropTargets.forEach((dropZone: RowDropZoneParams) => {
          gridRef.current!.api.removeRowDropZone(dropZone);
        });
      }

      const dropZoneParamsToSave: RowDropZoneParams[] = [];
      dragAndDropTargets.forEach((x) => {
        const dropZoneParams = {
          getContainer: () => x.element,
          onDragEnter: () =>
            (x.element.style["boxShadow"] = "0 0 0 2px rgb(24 74 144 / 60%), 0 2px 2px rgb(24 74 144 / 100%)"),
          onDragLeave: () => (x.element.style["boxShadow"] = ""),
          onDragStop: (params: RowDragEndEvent) => {
            x.element.style["boxShadow"] = "";
            const hasExistingSelection = assignmentState.selectedJobs.length > 0;

            if (hasExistingSelection) {
              // User had pre-selected rows before dragging - keep existing flow
              if (assignmentState.selectedManifest?.manifestDriverId !== x.manifest.manifestDriverId) {
                assignmentDispatch({type: "SetSelectedManifest", payload: x.manifest});
              }
              if (params.nodes.map((job) => job.data.jobId)?.length === 1) {
                assignmentDispatch({
                  type: "SetSelectedJob",
                  payload: {
                    pk: "",
                    sk: "",
                    ...params.node.data
                  } as unknown as Job
                });
              }
              if (driverRequirementsEnabled) {
                const selectedJobs: Job[] = params.nodes.map((node) => node.data);
                handleAssignJobs(selectedJobs, x.manifest);
              } else {
                checkEverCompletedBeforeAssign(
                  params.nodes.map((node) => node.data as Job),
                  assign
                );
              }
            } else {
              // User dragged without pre-selection - pass data directly to avoid bar flicker
              const draggedJobs = params.nodes.map((node) => node.data as Job);
              if (driverRequirementsEnabled) {
                handleAssignJobs(draggedJobs, x.manifest);
              } else {
                checkEverCompletedBeforeAssign(draggedJobs, () => assignDirect(draggedJobs, x.manifest));
              }
            }
          }
        };
        dropZoneParamsToSave.push(dropZoneParams);
        gridRef.current!.api.addRowDropZone(dropZoneParams);
      });
      assignmentViewDispatch({
        type: AssignmentViewActionTypes.SET_CURRENT_DRAG_AND_DROP_TARGETS,
        payload: dropZoneParamsToSave
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gridReady,
    isInitialized,
    dragAndDropTargets,
    assignmentDispatch,
    assignmentViewDispatch,
    assignJobsToManifest,
    assignmentState.selectedManifest?.manifestDriverId
  ]);

  // Update global filter when dispatch stations selection changes
  useEffect(() => {
    if (gridReady && isInitialized) {
      const newGridContext = {
        ...gridContext,
        globalFilter: hasSelectedDispatchStations(dispatchStationState)
          ? buildDispatchGroupFilter(
              "dispatchGroup_dispatchGroupId",
              jobGroups.map((jg) => Number(jg.dispatchGroupId))
            )
          : undefined
      };
      if (!isEqual(newGridContext, gridContext)) {
        setGridContext(newGridContext);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobGroups, setGridContext, isInitialized, gridReady, dispatchStationState]);

  // Update job group filter values when job groups change
  useEffect(() => {
    if (gridReady && isInitialized) {
      gridRef.current!.api.getFilterInstance<ISetFilter>("dispatchGroup.name", (filter) => {
        try {
          filter?.setFilterValues(jobGroups.map((d) => d.dispatchGroup!));

          const validSelectedJobGroups = selectedJobGroups
            ? selectedJobGroups.filter((sjg) => jobGroups.some((jg) => jg.dispatchGroup === sjg))
            : null;
          if ((validSelectedJobGroups === null || validSelectedJobGroups.length === 0) && selectedJobGroups !== null) {
            setSelectedJobGroups(null);
            filter?.applyModel("api");
            gridRef.current!.api.onFilterChanged();
          } else {
            setSelectedJobGroups(validSelectedJobGroups);
          }
        } catch (error) {
          // Handle case where virtual list has not been created yet (timing issue in tests)
          console.debug("SetFilter not ready:", error);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridReady, isInitialized, jobGroups, onRemoveFilters]);

  // Update grid filter when selected job groups change
  useEffect(() => {
    if (gridReady && isInitialized) {
      gridRef.current!.api.getFilterInstance<ISetFilter>("dispatchGroup.name", (filter) => {
        try {
          const newModel = selectedJobGroups === null ? null : {values: selectedJobGroups};
          if (!isEqual(filter?.getModel(), newModel)) {
            filter?.setModel(newModel);
            gridRef.current!.api.onFilterChanged();
          }
          setGridGroupInitialized(true);
        } catch (error) {
          // Handle case where virtual list has not been created yet (timing issue in tests)
          console.debug("SetFilter not ready for setModel:", error);
        }
      });
    }
  }, [gridReady, isInitialized, selectedJobGroups]);

  // Refresh filters when grid context changes
  useEffect(() => {
    if (gridReady && isInitialized && isGridGroupInitialized) {
      gridRef.current!.api.onFilterChanged();
    }
  }, [gridContext, gridReady, isGridGroupInitialized, isInitialized]);

  // Update map visibility for unassigned jobs when page or showing jobs change
  useEffect(() => {
    if (
      [UnassignedJobsVisibilityTypes.PICK_UP, UnassignedJobsVisibilityTypes.DELIVERIES].includes(
        mapVisibilityState.unassignedJobVisibilityType
      )
    ) {
      mapVisibilityDispatch({
        type: "SetAllUnassignedJobsVisibility",
        jobIds: assignmentViewState?.showingJobIds ?? []
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentViewState?.unassignedJobsPage, assignmentViewState?.showingJobIds]);

  // Update map visibility for jobs based on map visibility state
  useEffect(() => {
    if (gridReady && isInitialized) {
      mapVisibilityState.jobs.forEach((x) => {
        const row = gridRef.current!.api.getRowNode(x.entityId.toString());
        const isVisible = x.visibilityLevel !== EntityVisibilityLevel.NONE;
        const agJob = row?.data;
        if (agJob) {
          if (agJob.map?.color !== x.routeColor || agJob.map?.visible !== isVisible) {
            row?.setDataValue("map", {color: x.routeColor, visible: isVisible});
          }
        } else {
          mapVisibilityDispatch({type: "ClearJobVisibility", jobId: x.entityId});
        }
      });
    }
  }, [gridReady, isInitialized, mapVisibilityDispatch, mapVisibilityState.jobs]);

  return (
    <DispatchNotesProvider>
      <PanelContainer>
        <ActiveFilterDialog ref={jobDialogRefs} {...activeFilter} />
        <AssignJobsConfirmation
          testIdPrefix={"job-panel-assign-job"}
          isVisible={showAssignJobsConfirm}
          assignmentState={assignmentState}
          driverRequiredSetting={driverRequiredSetting}
          handleConfirm={handleAssignJobsConfirm}
          handleClose={() => {
            pendingConfirmActionRef.current = null;
            setShowAssignJobsConfirm(false);
          }}
          isSkillsBasedRequirements={isSkillsBased}
        />
        <EverCompletedCheckDialog
          testIdPrefix="job-panel-ever-completed"
          isVisible={showEverCompletedDialog}
          everCompletedJobs={everCompletedJobs}
          handleClose={() => {
            setShowEverCompletedDialog(false);
            setEverCompletedJobs([]);
          }}
        />
        <SuggestErrorDialog
          isOpen={showSuggestError}
          error={suggestedAssignment.error}
          validationErrors={suggestedAssignment.validationErrors}
          stops={assignmentState.selectedJobs[0]?.stops ?? []}
          onClose={() => {
            setShowSuggestError(false);
            suggestedAssignment.cancel();
          }}
        />
        <ButtonBar size={warningMessage ? 86 : 62} hasWarningMessage={Boolean(warningMessage)}>
          <ButtonContainer>
            <LeftControls>
              <StyledButton
                loading={isOpeningCMSOrder}
                intent="primary"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={openOrderEntry}
                endIcon={"plus"}
              />
              <MultiSelectDropDown
                testIdPrefix={"job-group-multiselect"}
                text={"Groups"}
                items={jobGroupOptions}
                onSelected={onJobGroupSelected}
                onUnselected={onJobGroupUnselected}
                onUnselectedAll={onJobGroupUnselectedAll}
                clearFilter
              />
              <ReadyDueTimeFilterSelect
                selectedOption={selectedReadyDueFilterOption}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onOptionSelected={() => {}}
                onConfirm={onConfirmSelectedTimeFilter}
                onClear={clearTimeFilter}
              />
              <PlacedInLastFilterSelect
                selectedOption={selectedPlacedInLastFilterOption}
                onOptionSelected={handleSelectedPlacedInLastTime}
                onClear={clearPlacedInLastTime}
              />
            </LeftControls>
            <RightControls>
              <ControlGroup>
                <Search
                  idPrefix={"jobs"}
                  onSearch={handleGlobalSearch}
                  isConstrainedByFilters={gridHasActiveFilters}
                  minimal
                />
                <Button
                  variant="minimal"
                  title={"Columns"}
                  icon={<FontAwesomeIcon style={{width: "16px", height: "14px"}} icon={solid("line-columns")} />}
                  onClick={() => {
                    openOrCloseToolPanel("columns");
                  }}
                />
                <Button
                  variant="minimal"
                  title={"Settings"}
                  icon={<FontAwesomeIcon style={{width: "16px", height: "15px"}} icon={solid("sliders")} />}
                  onClick={() => {
                    openOrCloseToolPanel("settings");
                  }}
                />
              </ControlGroup>
            </RightControls>
          </ButtonContainer>
          {warningMessage && (
            <WarningText style={{padding: "0 24px"}}>
              <WarningIcon />
              <Text ellipsize>{warningMessage}</Text>
            </WarningText>
          )}
        </ButtonBar>
        <Fill className={"ag-theme-alpine"}>
          <AgGridReact<Job>
            className={"jobs-panel-grid"}
            ref={gridRef}
            onGridReady={handleGridReady}
            getRowClass={(params) => getJobRowClass(params, assignmentState.pendingActions)}
            defaultColDef={DefaultColumnDef}
            columnDefs={columnDefs}
            context={gridContext}
            suppressCellFocus
            columnTypes={ColumnTypes}
            rowSelection={"multiple"}
            rowMultiSelectWithClick
            onRowSelected={handleSelectedRow}
            enableCellTextSelection
            ensureDomOrder
            suppressRowClickSelection={isSuggestionMode}
            suppressRowDrag={isSuggestionMode}
            rowDragMultiRow
            rowDragText={rowDragText}
            onRowDragEnter={onRowDragEnter}
            onRowDragEnd={onRowDragEnd}
            sideBar={sideBar}
            onColumnMoved={onColumnMoveChanged}
            onSortChanged={onColumnSortChanged}
            onColumnVisible={onColumnVisible}
            onColumnResized={onColumnMoveChanged}
            onFilterChanged={onFilterChanged}
            rowModelType={"serverSide"}
            maxBlocksInCache={2} // SSRM ignores this under pagination mode; kept as intent signal
            serverSideDatasource={datasource}
            pagination
            paginationPageSize={100}
            animateRows
            gridOptions={JobGridOptions}
            onCellClicked={onCellClicked}
            onPaginationChanged={onPaginationChanged}
            onFirstDataRendered={onFirstDataRendered}
            rowStyle={CustomRowStyle}
            maintainColumnOrder
            noRowsOverlayComponent={BadDataOverlay}
            suppressMultiSort={!multipleSortEnabled}
          />
        </Fill>
        {assignmentState.selectedJobs.length > 0 && (
          <AssignmentBarV2Pinned
            selectedManifest={assignmentState.selectedManifest}
            selectedRows={assignmentState.selectedJobs}
            assignText="Assign"
            isEnableQualified={isEnableQualified}
            onAssign={handleAssignJobs}
            onClearSelection={clearSelection}
            onQualify={toggleQualifiedDriversHandler}
            onSuggest={
              suggestedAssignmentEnabled &&
              assignmentState.selectedJobs.length === 1 &&
              !suggestedAssignment.isLoading &&
              (suggestedAssignment.usageRemaining === null || suggestedAssignment.usageRemaining > 0)
                ? () => {
                    assignmentDispatch({type: "SetSelectedManifest", payload: undefined});
                    suggestedAssignment.submit(assignmentState.selectedJobs[0].jobId);
                  }
                : undefined
            }
            suggestLoading={suggestedAssignment.isLoading}
            suggestState={suggestedAssignment.state}
            isSuggestActive={suggestedAssignment.isActive}
            isSuggestComplete={suggestedAssignment.state === "complete"}
            onClearSuggest={() => suggestedAssignment.cancel()}
          />
        )}
      </PanelContainer>
    </DispatchNotesProvider>
  );
};

export default JobPanel;
